import React, { useEffect, useState, Component } from 'react';
import { AssessmentResult } from '../types/assessment';
import { Button } from './ui/Button';
import { CheckCircle2, AlertCircle, BarChart2, ArrowUpRight, Target, ShieldCheck } from 'lucide-react';
import { PROFILE_CONTENT, ProfileContent } from '../data/profileContent';
import { SmsGate } from './SmsGate';
import { EmailCapture } from './EmailCapture';
interface ResultsScreenProps {
  result: AssessmentResult;
  isPreview?: boolean;
}
// Profile-specific background patterns - Simplified for dark theme
const HeroPattern = () => {
  return <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-162">
      <path d="M0 100 L100 0" stroke="#E10600" strokeWidth="0.5" data-id="element-163" />
      <path d="M20 100 L100 20" stroke="#E10600" strokeWidth="0.5" opacity="0.5" data-id="element-164" />
      <path d="M40 100 L100 40" stroke="#E10600" strokeWidth="0.5" opacity="0.3" data-id="element-165" />
      <path d="M-20 100 L80 0" stroke="#E10600" strokeWidth="0.5" opacity="0.5" data-id="element-166" />
    </svg>;
};
export function ResultsScreen({
  result,
  isPreview = false
}: ResultsScreenProps) {
  const [animateBars, setAnimateBars] = useState(false);
  const [unlocked, setUnlocked] = useState(isPreview);
  useEffect(() => {
    const timer = setTimeout(() => setAnimateBars(true), 300);
    return () => clearTimeout(timer);
  }, []);
  const content: ProfileContent = PROFILE_CONTENT[result.final_profile] || PROFILE_CONTENT['Review Manually'];
  const Icon = content.icon;
  const theme = content.theme;
  const SectionHeading = ({
    children,
    icon: IconComponent
  }: {
    children: React.ReactNode;
    icon?: any;
  }) => <div className="flex items-center mb-8" style={{
    borderLeft: `3px solid #E10600`
  }} data-id="element-167">
      <div className="pl-4 flex items-center" data-id="element-168">
        <h3 className="text-2xl font-display font-bold text-white tracking-wide uppercase" data-id="element-169">
          {children}
        </h3>
      </div>
    </div>;
  return <div className="max-w-4xl mx-auto px-4 py-8 animate-in fade-in duration-700" data-id="element-170">
      <div className="mb-12" data-id="element-171">
        {/* ===== HERO (always visible) ===== */}
        <div className="bg-[#0B0F14] border border-[#2a2f3b] rounded-md overflow-hidden relative mb-12" data-id="element-172">
          <div className="absolute inset-0 bg-gradient-to-br from-[#E10600]/5 to-transparent pointer-events-none" data-id="element-173" />
          <HeroPattern data-id="element-174" />

          <div className="relative z-10 px-6 py-16 md:py-20 text-center" data-id="element-175">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-[#E10600]/10 border border-[#E10600]/30 mb-8 shadow-[0_0_30px_rgba(225,6,0,0.2)]" data-id="element-176">
              <Icon size={48} className="text-[#E10600]" data-id="element-177" />
            </div>
            <h2 className="text-[#6B7280] font-bold tracking-[0.2em] uppercase text-xs mb-4" data-id="element-178">
              Your Sales Superpower
            </h2>
            <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-4 tracking-tight leading-none uppercase" data-id="element-179">
              {content.title}
            </h1>
            <p className="text-xl text-[#9ca3af] font-light max-w-2xl mx-auto leading-relaxed" data-id="element-180">
              {content.tagline}
            </p>
          </div>
        </div>

        {/* ===== TEASER: Summary (always visible) ===== */}
        <div className="mb-16 text-center max-w-3xl mx-auto" data-id="element-181">
          <p className="text-lg md:text-xl text-[#F5F5F4] leading-relaxed font-light" data-id="element-182">
            {content.summary}
          </p>
        </div>

        {/* ===== TEASER: Trait Breakdown (always visible) ===== */}
        <div className="mb-16 bg-[#111318] rounded-md p-8 border border-[#2a2f3b]" data-id="element-183">
          <div className="flex items-center justify-center mb-8 text-white" data-id="element-184">
            <BarChart2 className="w-5 h-5 mr-3 text-[#E10600]" data-id="element-185" />
            <h3 className="font-display font-bold text-2xl tracking-wide uppercase" data-id="element-186">
              Your Superpower Breakdown
            </h3>
          </div>

          <div className="space-y-8 max-w-2xl mx-auto" data-id="element-187">
            <div data-id="element-188">
              <div className="flex justify-between items-end mb-3" data-id="element-189">
                <span className="text-[#6B7280] text-xs font-bold uppercase tracking-widest" data-id="element-190">
                  Drive & Resilience
                </span>
                <span className="text-white font-display font-bold text-3xl leading-none" data-id="element-191">
                  {result.drive_pct}%
                </span>
              </div>
              <div className="h-2 w-full bg-[#1a1f2b] rounded-sm overflow-hidden" data-id="element-192">
                <div className="h-full bg-[#E10600] transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(225,6,0,0.5)]" style={{
                width: animateBars ? `${result.drive_pct}%` : '0%'
              }} data-id="element-193" />
              </div>
            </div>

            <div data-id="element-194">
              <div className="flex justify-between items-end mb-3" data-id="element-195">
                <span className="text-[#6B7280] text-xs font-bold uppercase tracking-widest" data-id="element-196">
                  Influence & Persuasion
                </span>
                <span className="text-white font-display font-bold text-3xl leading-none" data-id="element-197">
                  {result.di_pct}%
                </span>
              </div>
              <div className="h-2 w-full bg-[#1a1f2b] rounded-sm overflow-hidden" data-id="element-198">
                <div className="h-full bg-[#E10600] transition-all duration-1000 ease-out delay-150 shadow-[0_0_10px_rgba(225,6,0,0.5)]" style={{
                width: animateBars ? `${result.di_pct}%` : '0%'
              }} data-id="element-199" />
              </div>
            </div>

            <div data-id="element-200">
              <div className="flex justify-between items-end mb-3" data-id="element-201">
                <span className="text-[#6B7280] text-xs font-bold uppercase tracking-widest" data-id="element-202">
                  Steadiness & Process
                </span>
                <span className="text-white font-display font-bold text-3xl leading-none" data-id="element-203">
                  {result.sc_pct}%
                </span>
              </div>
              <div className="h-2 w-full bg-[#1a1f2b] rounded-sm overflow-hidden" data-id="element-204">
                <div className="h-full bg-[#E10600] transition-all duration-1000 ease-out delay-300 shadow-[0_0_10px_rgba(225,6,0,0.5)]" style={{
                width: animateBars ? `${result.sc_pct}%` : '0%'
              }} data-id="element-205" />
              </div>
            </div>
          </div>
        </div>

        {/* ===== GATE: SMS Verification ===== */}
        {!unlocked && <div className="relative mb-12" data-id="element-206">
            {/* Blurred preview of what's behind the gate */}
            <div className="filter blur-sm opacity-20 select-none pointer-events-none mb-8 space-y-8" data-id="element-207">
              <div className="flex items-center" style={{
            borderLeft: `3px solid #E10600`
          }} data-id="element-208">
                <div className="pl-4" data-id="element-209">
                  <h3 className="text-2xl font-display font-bold text-white uppercase" data-id="element-210">
                    Your Personalized Playbook
                  </h3>
                </div>
              </div>
              <div className="space-y-3 pl-4" data-id="element-211">
                <div className="h-4 bg-[#1a1f2b] rounded w-3/4" data-id="element-212"></div>
                <div className="h-4 bg-[#1a1f2b] rounded w-2/3" data-id="element-213"></div>
                <div className="h-4 bg-[#1a1f2b] rounded w-4/5" data-id="element-214"></div>
              </div>
              <div className="flex items-center" style={{
            borderLeft: `3px solid #E10600`
          }} data-id="element-215">
                <div className="pl-4" data-id="element-216">
                  <h3 className="text-2xl font-display font-bold text-white uppercase" data-id="element-217">
                    AI Coaching Insights
                  </h3>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-218">
                <div className="h-24 bg-[#1a1f2b] rounded-md" data-id="element-219"></div>
                <div className="h-24 bg-[#1a1f2b] rounded-md" data-id="element-220"></div>
                <div className="h-24 bg-[#1a1f2b] rounded-md" data-id="element-221"></div>
                <div className="h-24 bg-[#1a1f2b] rounded-md" data-id="element-222"></div>
              </div>
            </div>

            {/* SMS Gate overlay */}
            <div className="relative -mt-64 z-20" data-id="element-223">
              <SmsGate onUnlock={() => setUnlocked(true)} barColor="#E10600" data-id="element-224" />
            </div>
          </div>}

        {/* ===== FULL RESULTS (only when unlocked) ===== */}
        {unlocked && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-225">
            {/* Where You're Most Likely to Succeed */}
            <div className="mb-16" data-id="element-226">
              <SectionHeading data-id="element-227">
                Where You're Most Likely to Succeed
              </SectionHeading>
              <ul className="space-y-4 pl-4" data-id="element-228">
                {content.successRoles.map((role, idx) => <li key={idx} className="flex items-start text-[#9ca3af]" data-id="element-229">
                    <CheckCircle2 className="w-5 h-5 mr-4 text-[#E10600] flex-shrink-0 mt-0.5" data-id="element-230" />
                    <span className="text-lg" data-id="element-231">{role}</span>
                  </li>)}
              </ul>
            </div>

            {/* Where You May Feel Friction */}
            <div className="mb-16" data-id="element-232">
              <SectionHeading data-id="element-233">Where You May Feel Friction</SectionHeading>
              <ul className="space-y-4 pl-4" data-id="element-234">
                {content.frictionRoles.map((role, idx) => <li key={idx} className="flex items-start text-[#6B7280]" data-id="element-235">
                    <AlertCircle className="w-5 h-5 mr-4 text-[#6B7280] flex-shrink-0 mt-0.5" data-id="element-236" />
                    <span className="text-lg" data-id="element-237">{role}</span>
                  </li>)}
              </ul>
            </div>

            {/* Why This Matters */}
            <div className="mb-16 bg-[#111318] p-8 rounded-md border border-[#2a2f3b]" data-id="element-238">
              <h4 className="font-display font-bold text-white text-xl mb-4 tracking-wide uppercase" data-id="element-239">
                Why This Matters
              </h4>
              <p className="text-[#9ca3af] leading-relaxed text-lg font-light" data-id="element-240">
                {content.whyItMatters}
              </p>
            </div>

            {/* Your Strengths */}
            <div className="mb-16" data-id="element-241">
              <SectionHeading data-id="element-242">Your Strengths</SectionHeading>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-243">
                {content.strengths.map((strength, idx) => <div key={idx} className="bg-[#111318] p-6 rounded-md border border-[#2a2f3b] flex items-start hover:border-[#E10600]/50 transition-colors group" data-id="element-244">
                    <div className="w-8 h-8 rounded-full bg-[#E10600]/10 text-[#E10600] flex items-center justify-center mr-4 flex-shrink-0 mt-0.5 group-hover:bg-[#E10600] group-hover:text-white transition-colors" data-id="element-245">
                      <CheckCircle2 size={16} data-id="element-246" />
                    </div>
                    <span className="text-[#F5F5F4] font-medium" data-id="element-247">
                      {strength}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Potential Growth Areas */}
            <div className="mb-16" data-id="element-248">
              <SectionHeading data-id="element-249">Potential Growth Areas</SectionHeading>
              <div className="space-y-4 pl-4" data-id="element-250">
                {content.growthAreas.map((area, idx) => <div key={idx} className="flex items-start text-[#9ca3af]" data-id="element-251">
                    <AlertCircle className="w-5 h-5 mr-4 text-[#E10600] flex-shrink-0 mt-0.5" data-id="element-252" />
                    <span className="text-lg" data-id="element-253">{area}</span>
                  </div>)}
              </div>
            </div>

            {/* How to Maximize Your Success */}
            <div className="mb-16" data-id="element-254">
              <SectionHeading data-id="element-255">How to Maximize Your Success</SectionHeading>
              <div className="grid md:grid-cols-2 gap-10 pl-4" data-id="element-256">
                <div data-id="element-257">
                  <h4 className="font-bold text-white mb-6 flex items-center text-sm uppercase tracking-widest border-b border-[#2a2f3b] pb-2" data-id="element-258">
                    <ArrowUpRight className="w-4 h-4 mr-2 text-[#E10600]" data-id="element-259" />
                    Behaviors to Lean Into
                  </h4>
                  <ul className="space-y-4" data-id="element-260">
                    {content.leanInto.map((item, idx) => <li key={idx} className="flex items-start text-[#9ca3af]" data-id="element-261">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#E10600] mr-3 mt-2 flex-shrink-0 shadow-[0_0_5px_#E10600]" data-id="element-262"></span>
                        {item}
                      </li>)}
                  </ul>
                </div>
                <div data-id="element-263">
                  <h4 className="font-bold text-white mb-6 flex items-center text-sm uppercase tracking-widest border-b border-[#2a2f3b] pb-2" data-id="element-264">
                    <Target className="w-4 h-4 mr-2 text-[#E10600]" data-id="element-265" />
                    Skills to Develop
                  </h4>
                  <ul className="space-y-4" data-id="element-266">
                    {content.develop.map((item, idx) => <li key={idx} className="flex items-start text-[#9ca3af]" data-id="element-267">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#6B7280] mr-3 mt-2 flex-shrink-0" data-id="element-268"></span>
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>

            {/* Confidence Indicator */}
            <div className="mb-16 bg-[#111318] border border-[#2a2f3b] rounded-md p-8" data-id="element-269">
              <div className="flex items-center mb-4" data-id="element-270">
                <ShieldCheck className="w-6 h-6 mr-3 text-[#6B7280]" data-id="element-271" />
                <h4 className="font-display font-bold text-white text-xl tracking-wide uppercase" data-id="element-272">
                  Profile Confidence:{' '}
                  <span className={result.confidence_level === 'High' ? 'text-[#E10600]' : result.confidence_level === 'Moderate' ? 'text-[#E10600]/80' : 'text-[#6B7280]'} data-id="element-273">
                    {result.confidence_level}
                  </span>
                </h4>
              </div>
              <p className="text-sm text-[#9ca3af]" data-id="element-274">
                {result.confidence_level === 'High' && 'Your answers were highly consistent across both question types, indicating strong self-awareness.'}
                {result.confidence_level === 'Moderate' && 'Your answers showed reasonable consistency. Some variation between question types is normal.'}
                {result.confidence_level === 'Low' && 'Your answers showed notable variation between question types. This may affect the precision of your profile.'}
              </p>
            </div>

            {/* Credibility & Comparative Note */}
            <div className="mb-16 text-center space-y-6" data-id="element-275">
              <p className="text-sm text-[#6B7280] italic max-w-2xl mx-auto" data-id="element-276">
                "This profile is based on behavioral patterns commonly observed
                in high-performing sales professionals. Your superpowers are
                unique to you — understanding them is the first step to
                leveraging them."
              </p>
              <div className="inline-block bg-[#1a1f2b] px-6 py-3 rounded-md text-xs font-bold text-[#9ca3af] border border-[#2a2f3b] uppercase tracking-wider" data-id="element-277">
                {content.comparativeNote}
              </div>
            </div>

            {/* Email Capture CTA */}
            <div className="bg-[#0B0F14] border border-[#2a2f3b] rounded-md p-10 text-center shadow-2xl relative overflow-hidden" data-id="element-278">
              <div className="absolute inset-0 bg-gradient-to-br from-[#E10600]/10 to-transparent pointer-events-none" data-id="element-279" />
              <div className="relative z-10" data-id="element-280">
                <div className="inline-flex items-center gap-2 bg-[#E10600]/10 border border-[#E10600]/30 rounded-md px-4 py-2 mb-6" data-id="element-281">
                  <span className="text-[#E10600] text-xs font-bold uppercase tracking-widest" data-id="element-282">
                    Included in your free trial
                  </span>
                </div>
                <h3 className="text-3xl font-display font-bold text-white mb-4 uppercase tracking-wide" data-id="element-283">
                  Get Your Full Superpower Playbook
                </h3>
                <p className="text-[#9ca3af] mb-8 max-w-lg mx-auto" data-id="element-284">
                  Your personalized playbook includes detailed strategies to
                  leverage your strengths, an AI-generated coaching plan, and
                  role-specific guidance — delivered straight to your inbox.
                </p>
                <div className="max-w-md mx-auto" data-id="element-285">
                  <EmailCapture profileColor={content.color} data-id="element-286" />
                </div>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}